import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import {
  ArrowLeft, Save, Package, Building2, Search, Plus, Trash2, Calculator,
  FileText, Globe, DollarSign, Ship, Plane, Truck, AlertCircle, CheckCircle, AlertTriangle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import api from '@/services/api';
import HighlightText from '@/components/HighlightText';

const currencies = [
  { value: 'USD', label: 'USD - US Dollar', symbol: '$' },
  { value: 'EUR', label: 'EUR - Euro', symbol: '€' },
  { value: 'GBP', label: 'GBP - British Pound', symbol: '£' },
  { value: 'AED', label: 'AED - UAE Dirham', symbol: 'AED' },
  { value: 'SGD', label: 'SGD - Singapore Dollar', symbol: 'S$' },
  { value: 'JPY', label: 'JPY - Japanese Yen', symbol: '¥' },
  { value: 'CNY', label: 'CNY - Chinese Yuan', symbol: '¥' }
];

const CreateGRNInternationalPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id: editGrnId } = useParams();
  const isEdit = Boolean(editGrnId);
  const poId = searchParams.get('po_id');
  const { toast } = useToast();

  // Form state
  const [vendor, setVendor] = useState(null);
  const [warehouse, setWarehouse] = useState(null);
  const [grnNumber, setGrnNumber] = useState('');
  const [grnStatus, setGrnStatus] = useState(null);
  const [linkedPoId, setLinkedPoId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [receivedDate, setReceivedDate] = useState(new Date().toISOString().split('T')[0]);
  const [importedDate, setImportedDate] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [dcNumber, setDcNumber] = useState('');
  const [otherDocRef, setOtherDocRef] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [conversionRate, setConversionRate] = useState(83.50);

  // Header Landed Costs (distributed across items)
  const [cfCharges, setCfCharges] = useState(0);
  const [chaCharges, setChaCharges] = useState(0);
  const [oceanFreight, setOceanFreight] = useState(0);
  const [airFreight, setAirFreight] = useState(0);
  const [otherFreightCharges, setOtherFreightCharges] = useState(0);
  const [otherFreightNotes, setOtherFreightNotes] = useState('');
  const [fssaiSamplingCharges, setFssaiSamplingCharges] = useState(0);
  const [transportCharges, setTransportCharges] = useState(0);
  const [transportNotes, setTransportNotes] = useState('');
  const [otherLandedCharges, setOtherLandedCharges] = useState(0);
  const [otherLandedNotes, setOtherLandedNotes] = useState('');

  // Items
  const [items, setItems] = useState([]);
  const [billDiscountPercent, setBillDiscountPercent] = useState(0);
  const [billDiscountAmount, setBillDiscountAmount] = useState(0);
  const [roundOff, setRoundOff] = useState(0);
  const [notes, setNotes] = useState('');

  // UI state
  const [vendors, setVendors] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [po, setPo] = useState(null);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const searchReqRef = useRef(0);
  const [securityCode, setSecurityCode] = useState('');
  const [marginBlock, setMarginBlock] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load vendors and warehouses
  useEffect(() => {
    const loadData = async () => {
      try {
        const [vendorsRes, warehousesRes] = await Promise.all([
          api.get('/vendors', { params: { is_active: true, is_international: true } }),
          api.get('/warehouses', { params: { is_active: true } })
        ]);
        setVendors(vendorsRes.data || []);
        const whList = warehousesRes.data || [];
        setWarehouses(whList);
        if (whList.length === 1) setWarehouse(prev => prev || whList[0]);
      } catch (error) {
        console.error('Failed to load data:', error);
      }
    };
    loadData();
  }, []);

  // Load PO if provided
  useEffect(() => {
    const loadPO = async () => {
      if (!poId || isEdit) return;
      try {
        const res = await api.get(`/po-v2/${poId}`);
        const poData = res.data;
        setPo(poData);
        
        // Pre-fill from PO
        if (poData.vendor_snapshot) {
          setVendor(poData.vendor_snapshot);
        }
        if (poData.warehouse_id) {
          const wh = warehouses.find(w => w.id === poData.warehouse_id);
          if (wh) setWarehouse(wh);
        }
        if (poData.currency) setCurrency(poData.currency);
        if (poData.conversion_rate) setConversionRate(poData.conversion_rate);

        // Pre-fill items from PO
        if (poData.items) {
          const grnItems = poData.items.map(item => ({
            product_id: item.product_id,
            product_name: item.product_name,
            sku: item.sku,
            hsn_code: item.hsn_code,
            uom: item.uom || 'PCS',
            ordered_qty: item.qty,
            received_qty: item.qty,
            accepted_qty: item.qty,
            rejected_qty: 0,
            rejection_reason: '',
            unit_price_fcy: item.unit_price,
            custom_duty_percent: 0,
            swc_percent: 10,
            other_duty_amount: 0,
            fssai_charges: 0,
            gst_percent: item.gst_percent || 0,
            discount_percent: item.discount_percent || 0,
            discount_amount: 0,
            gross_discount_amount: 0,
            scheme_discount_percent: 0,
            scheme_discount_amount: 0,
            gross_scheme_discount_amount: 0,
            lot_batch_number: '',
            manufacturing_date: '',
            best_before_date: '',
            expiry_date: '',
            mrp: 0
          }));
          setItems(grnItems);
        }
      } catch (error) {
        toast({ title: 'Error', description: 'Failed to load PO', variant: 'destructive' });
      }
    };
    if (warehouses.length > 0) loadPO();
  }, [poId, warehouses, toast, isEdit]);

  // Load existing GRN for edit mode
  useEffect(() => {
    if (!isEdit) return;
    const loadGRN = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/grn-v2/international/${editGrnId}`);
        const g = res.data;
        if (!['draft', 'pending_verification'].includes(g.status)) {
          toast({
            title: 'Not editable',
            description: 'Only Draft or Pending Verification GRNs can be edited',
            variant: 'destructive'
          });
          navigate('/hq/purchase/grn');
          return;
        }
        setGrnNumber(g.grn_number || '');
        setGrnStatus(g.status);
        setLinkedPoId(g.po_id || null);
        setPo(g.po_number ? { po_number: g.po_number } : null);
        setVendor({ id: g.vendor_id, ...(g.vendor_snapshot || {}) });
        setWarehouse({ id: g.warehouse_id, name: g.warehouse_name });
        setReceivedDate(g.received_date || new Date().toISOString().split('T')[0]);
        setImportedDate(g.imported_date || '');
        setInvoiceNumber(g.invoice_number || '');
        setDcNumber(g.dc_number || '');
        setOtherDocRef(g.other_doc_reference || '');
        setCurrency(g.currency || 'USD');
        setConversionRate(g.conversion_rate || 83.5);
        setCfCharges(g.cf_charges || 0);
        setChaCharges(g.cha_charges || 0);
        setOceanFreight(g.ocean_freight || 0);
        setAirFreight(g.air_freight || 0);
        setOtherFreightCharges(g.other_freight_charges || 0);
        setOtherFreightNotes(g.other_freight_notes || '');
        setFssaiSamplingCharges(g.fssai_sampling_charges || 0);
        setTransportCharges(g.transport_charges || 0);
        setTransportNotes(g.transport_notes || '');
        setOtherLandedCharges(g.other_landed_charges || 0);
        setOtherLandedNotes(g.other_landed_notes || '');
        setBillDiscountPercent(g.bill_discount_percent || 0);
        setBillDiscountAmount(g.bill_discount_percent ? 0 : (g.bill_discount_amount || 0));
        setRoundOff(g.round_off || 0);
        setNotes(g.notes || '');
        setItems((g.items || []).map(it => ({
          product_id: it.product_id,
          product_name: it.product_name,
          sku: it.sku,
          hsn_code: it.hsn_code || '',
          uom: it.uom || 'PCS',
          ordered_qty: it.ordered_qty || 0,
          received_qty: it.received_qty || 0,
          accepted_qty: it.accepted_qty || 0,
          rejected_qty: it.rejected_qty || 0,
          rejection_reason: it.rejection_reason || '',
          unit_price_fcy: it.unit_price_fcy || 0,
          custom_duty_percent: it.custom_duty_percent || 0,
          swc_percent: it.swc_percent || 0,
          other_duty_amount: it.other_duty_amount || 0,
          fssai_charges: it.fssai_charges || 0,
          gst_percent: it.gst_percent || 0,
          discount_percent: it.discount_percent || 0,
          discount_amount: it.discount_percent ? 0 : (it.discount_amount || 0),
          gross_discount_amount: it.gross_discount_amount || 0,
          scheme_discount_percent: it.scheme_discount_percent || 0,
          scheme_discount_amount: it.scheme_discount_amount || 0,
          gross_scheme_discount_amount: it.gross_scheme_discount_amount || 0,
          lot_batch_number: it.lot_batch_number || '',
          manufacturing_date: it.manufacturing_date || '',
          best_before_date: it.best_before_date || '',
          expiry_date: it.expiry_date || '',
          mrp: it.mrp || 0
        })));
      } catch (error) {
        toast({
          title: 'Error',
          description: error.response?.data?.detail || 'Failed to load GRN',
          variant: 'destructive'
        });
      } finally {
        setLoading(false);
      }
    };
    loadGRN();
  }, [isEdit, editGrnId, navigate, toast]);

  // Search products
  const handleProductSearch = useCallback(async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearchLoading(true);
    const reqId = ++searchReqRef.current;
    try {
      const res = await api.get('/products', { params: { search: query.trim(), limit: 20 } });
      if (reqId !== searchReqRef.current) return;
      const products = res.data?.items || res.data || [];
      setSearchResults(products);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => handleProductSearch(productSearch), 300);
    return () => clearTimeout(timer);
  }, [productSearch, handleProductSearch]);

  // Add product to items
  const handleAddProduct = (product) => {
    // Capture scanned barcode if search text matches one of the product's barcodes
    const scanInput = (productSearch || '').trim();
    const allBarcodes = [product.barcode, ...(product.barcodes || []).map(b => b.barcode)].filter(Boolean);
    const scannedBarcode = scanInput && allBarcodes.includes(scanInput) ? scanInput : '';

    const dup = items.find(i => {
      if (i.product_id !== product.id) return false;
      if (scannedBarcode) return (i.scanned_barcode || '') === scannedBarcode;
      return true;
    });
    if (dup) {
      toast({
        title: scannedBarcode ? 'Same barcode already scanned' : 'Already added',
        description: scannedBarcode
          ? `Barcode ${scannedBarcode} for ${product.name} is already in the GRN. Scan a different barcode or edit the existing row.`
          : `${product.name} is already entered in this GRN. Edit the existing row instead of adding it again.`,
        variant: 'destructive',
      });
      return;
    }

    setItems(prev => [...prev, {
      product_id: product.id,
      product_name: product.name,
      sku: product.sku,
      hsn_code: product.hsn_code,
      scanned_barcode: scannedBarcode,
      uom: product.purchase_uom || product.base_uom || 'PCS',
      ordered_qty: 0,
      received_qty: 0,
      accepted_qty: 0,
      rejected_qty: 0,
      rejection_reason: '',
      unit_price_fcy: 0,
      custom_duty_percent: 0,
      swc_percent: 10,
      other_duty_amount: 0,
      fssai_charges: 0,
      gst_percent: 18,
      discount_percent: 0,
      discount_amount: 0,
      gross_discount_amount: 0,
      scheme_discount_percent: 0,
      scheme_discount_amount: 0,
      gross_scheme_discount_amount: 0,
      lot_batch_number: '',
      manufacturing_date: '',
      best_before_date: '',
      expiry_date: '',
      mrp: 0
    }]);
    setProductDialogOpen(false);
    setProductSearch('');
  };

  // Update item field
  const updateItem = (index, field, value) => {
    setItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      // Auto-sync accepted_qty with received_qty if not manually set
      if (field === 'received_qty') {
        updated[index].accepted_qty = value;
      }
      // If quantity or unit price changes, refresh discount amounts keeping % as driver
      if (['received_qty', 'accepted_qty', 'unit_price_fcy'].includes(field)) {
        const recomputed = recomputeDiscountFieldsIntl(updated[index], field);
        updated[index] = { ...updated[index], ...recomputed };
      }
      return updated;
    });
  };
  
  // ---- Discount recomputation (International: uses unit_price_inr as base) ----
  const roundI2 = (n) => Math.round((parseFloat(n) || 0) * 100) / 100;
  
  const recomputeDiscountFieldsIntl = (item, changed) => {
    const qty = parseFloat(item.accepted_qty ?? item.received_qty) || 0;
    const unitPriceInr = (parseFloat(item.unit_price_fcy) || 0) * (parseFloat(conversionRate) || 1);
    
    let discPct = parseFloat(item.discount_percent) || 0;
    let discAmt = parseFloat(item.discount_amount) || 0;
    let grossDisc = parseFloat(item.gross_discount_amount) || 0;
    
    if (changed === 'discount_percent') {
      discAmt = unitPriceInr > 0 ? roundI2(unitPriceInr * discPct / 100) : 0;
      grossDisc = roundI2(qty * discAmt);
    } else if (changed === 'discount_amount') {
      discPct = unitPriceInr > 0 ? roundI2(discAmt / unitPriceInr * 100) : 0;
      grossDisc = roundI2(qty * discAmt);
    } else if (changed === 'gross_discount_amount') {
      discAmt = qty > 0 ? roundI2(grossDisc / qty) : 0;
      discPct = unitPriceInr > 0 ? roundI2(discAmt / unitPriceInr * 100) : 0;
    } else {
      discAmt = unitPriceInr > 0 ? roundI2(unitPriceInr * discPct / 100) : 0;
      grossDisc = roundI2(qty * discAmt);
    }
    
    const postDiscUnit = Math.max(0, unitPriceInr - discAmt);
    
    let schPct = parseFloat(item.scheme_discount_percent) || 0;
    let schAmt = parseFloat(item.scheme_discount_amount) || 0;
    let grossSch = parseFloat(item.gross_scheme_discount_amount) || 0;
    
    if (changed === 'scheme_discount_percent') {
      schAmt = postDiscUnit > 0 ? roundI2(postDiscUnit * schPct / 100) : 0;
      grossSch = roundI2(qty * schAmt);
    } else if (changed === 'scheme_discount_amount') {
      schPct = postDiscUnit > 0 ? roundI2(schAmt / postDiscUnit * 100) : 0;
      grossSch = roundI2(qty * schAmt);
    } else if (changed === 'gross_scheme_discount_amount') {
      schAmt = qty > 0 ? roundI2(grossSch / qty) : 0;
      schPct = postDiscUnit > 0 ? roundI2(schAmt / postDiscUnit * 100) : 0;
    } else {
      schAmt = postDiscUnit > 0 ? roundI2(postDiscUnit * schPct / 100) : 0;
      grossSch = roundI2(qty * schAmt);
    }
    
    return {
      discount_percent: discPct,
      discount_amount: discAmt,
      gross_discount_amount: grossDisc,
      scheme_discount_percent: schPct,
      scheme_discount_amount: schAmt,
      gross_scheme_discount_amount: grossSch,
    };
  };
  
  const updateDiscountFieldIntl = (index, field, value) => {
    setItems(prev => {
      const updated = [...prev];
      const v = value === '' ? 0 : parseFloat(value);
      updated[index] = { ...updated[index], [field]: isNaN(v) ? 0 : v };
      const recomputed = recomputeDiscountFieldsIntl(updated[index], field);
      updated[index] = { ...updated[index], ...recomputed };
      return updated;
    });
  };

  // Remove item
  const removeItem = (index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  // Calculate totals
  const totalHeaderLanded = 
    parseFloat(cfCharges || 0) + 
    parseFloat(chaCharges || 0) + 
    parseFloat(oceanFreight || 0) + 
    parseFloat(airFreight || 0) + 
    parseFloat(otherFreightCharges || 0) + 
    parseFloat(fssaiSamplingCharges || 0) + 
    parseFloat(transportCharges || 0) + 
    parseFloat(otherLandedCharges || 0);

  const totalQty = items.reduce((sum, i) => sum + (parseFloat(i.accepted_qty) || 0), 0);

  const calculateItemTotals = (item) => {
    const acceptedQty = parseFloat(item.accepted_qty) || 0;
    const unitPriceFcy = parseFloat(item.unit_price_fcy) || 0;
    const rate = parseFloat(conversionRate) || 1;
    
    const unitPriceInr = unitPriceFcy * rate;
    const assessableValue = acceptedQty * unitPriceInr;
    
    // Duties
    const customDutyAmt = (assessableValue * (parseFloat(item.custom_duty_percent) || 0)) / 100;
    const swcAmt = (customDutyAmt * (parseFloat(item.swc_percent) || 0)) / 100;
    const totalDuty = customDutyAmt + swcAmt + (parseFloat(item.other_duty_amount) || 0) + (parseFloat(item.fssai_charges) || 0);
    
    // GST (on assessable + duty)
    const gstBase = assessableValue + totalDuty;
    const gstAmt = (gstBase * (parseFloat(item.gst_percent) || 0)) / 100;
    
    // Header landed cost share
    const lineShare = totalQty > 0 ? (totalHeaderLanded * acceptedQty) / totalQty : 0;
    
    // Discount = per-line gross discount + gross scheme discount (Option A);
    // fallback to legacy percent/amount if the new fields are zero.
    const legacyDisc = (assessableValue * (parseFloat(item.discount_percent) || 0)) / 100
                       + (parseFloat(item.discount_amount) || 0);
    const newDisc = (parseFloat(item.gross_discount_amount) || 0)
                    + (parseFloat(item.gross_scheme_discount_amount) || 0);
    const discAmt = newDisc > 0 ? newDisc : legacyDisc;
    
    // Line total
    const lineTotal = assessableValue + totalDuty + gstAmt + lineShare - discAmt;
    
    // Avg base cost (landed cost per unit)
    const avgBaseCost = acceptedQty > 0 ? 
      (unitPriceInr + (totalDuty / acceptedQty) + (lineShare / acceptedQty)) : 0;

    return {
      unitPriceInr: unitPriceInr.toFixed(2),
      assessableValue: assessableValue.toFixed(2),
      customDutyAmt: customDutyAmt.toFixed(2),
      swcAmt: swcAmt.toFixed(2),
      totalDuty: totalDuty.toFixed(2),
      gstAmt: gstAmt.toFixed(2),
      lineShare: lineShare.toFixed(2),
      lineTotal: lineTotal.toFixed(2),
      avgBaseCost: avgBaseCost.toFixed(4)
    };
  };

  // Grand totals
  const grandTotals = items.reduce((acc, item) => {
    const calc = calculateItemTotals(item);
    return {
      subtotalFcy: acc.subtotalFcy + (parseFloat(item.accepted_qty) || 0) * (parseFloat(item.unit_price_fcy) || 0),
      subtotalInr: acc.subtotalInr + parseFloat(calc.assessableValue),
      totalDuty: acc.totalDuty + parseFloat(calc.totalDuty),
      totalGst: acc.totalGst + parseFloat(calc.gstAmt),
      lineTotal: acc.lineTotal + parseFloat(calc.lineTotal)
    };
  }, { subtotalFcy: 0, subtotalInr: 0, totalDuty: 0, totalGst: 0, lineTotal: 0 });

  const billDisc = (grandTotals.subtotalInr * (parseFloat(billDiscountPercent) || 0)) / 100 + (parseFloat(billDiscountAmount) || 0);
  const grandTotal = grandTotals.lineTotal - billDisc + parseFloat(roundOff || 0);

  // Save GRN
  const handleSave = async (andComplete = false, codeOverride = null) => {
    // Validation
    if (!vendor) {
      toast({ title: 'Error', description: 'Please select a vendor', variant: 'destructive' });
      return;
    }
    if (!warehouse) {
      toast({ title: 'Error', description: 'Please select a warehouse', variant: 'destructive' });
      return;
    }
    if (items.length === 0) {
      toast({ title: 'Error', description: 'Please add at least one item', variant: 'destructive' });
      return;
    }
    
    // Validate batch fields
    for (const item of items) {
      if (!item.lot_batch_number || !item.expiry_date || !item.mrp) {
        toast({ 
          title: 'Error', 
          description: `${item.product_name}: Batch number, expiry date, and MRP are required`,
          variant: 'destructive'
        });
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        vendor_id: vendor.vendor_id || vendor.id,
        warehouse_id: warehouse.id,
        po_id: poId || linkedPoId || null,
        received_date: receivedDate,
        imported_date: importedDate || null,
        invoice_number: invoiceNumber || null,
        dc_number: dcNumber || null,
        other_doc_reference: otherDocRef || null,
        currency,
        conversion_rate: parseFloat(conversionRate),
        cf_charges: parseFloat(cfCharges) || 0,
        cha_charges: parseFloat(chaCharges) || 0,
        ocean_freight: parseFloat(oceanFreight) || 0,
        air_freight: parseFloat(airFreight) || 0,
        other_freight_charges: parseFloat(otherFreightCharges) || 0,
        other_freight_notes: otherFreightNotes || null,
        fssai_sampling_charges: parseFloat(fssaiSamplingCharges) || 0,
        transport_charges: parseFloat(transportCharges) || 0,
        transport_notes: transportNotes || null,
        other_landed_charges: parseFloat(otherLandedCharges) || 0,
        other_landed_notes: otherLandedNotes || null,
        items: items.map(item => ({
          product_id: item.product_id,
          received_qty: parseInt(item.received_qty) || 0,
          accepted_qty: parseInt(item.accepted_qty) || 0,
          rejected_qty: parseInt(item.rejected_qty) || 0,
          rejection_reason: item.rejection_reason || null,
          unit_price_fcy: parseFloat(item.unit_price_fcy) || 0,
          currency: currency,
          conversion_rate: parseFloat(conversionRate),
          custom_duty_percent: parseFloat(item.custom_duty_percent) || 0,
          swc_percent: parseFloat(item.swc_percent) || 0,
          other_duty_amount: parseFloat(item.other_duty_amount) || 0,
          fssai_charges: parseFloat(item.fssai_charges) || 0,
          gst_percent: parseFloat(item.gst_percent) || 0,
          discount_percent: parseFloat(item.discount_percent) || 0,
          discount_amount: parseFloat(item.discount_amount) || 0,
          gross_discount_amount: parseFloat(item.gross_discount_amount) || 0,
          scheme_discount_percent: parseFloat(item.scheme_discount_percent) || 0,
          scheme_discount_amount: parseFloat(item.scheme_discount_amount) || 0,
          gross_scheme_discount_amount: parseFloat(item.gross_scheme_discount_amount) || 0,
          hsn_code: item.hsn_code || null,
          scanned_barcode: item.scanned_barcode || null,
          lot_batch_number: item.lot_batch_number,
          manufacturing_date: item.manufacturing_date || null,
          best_before_date: item.best_before_date || null,
          expiry_date: item.expiry_date,
          mrp: parseFloat(item.mrp) || 0
        })),
        bill_discount_percent: parseFloat(billDiscountPercent) || 0,
        bill_discount_amount: parseFloat(billDiscountAmount) || 0,
        round_off: parseFloat(roundOff) || 0,
        security_code: codeOverride || securityCode || null,
        notes: notes || null
      };

      if (isEdit) {
        await api.put(`/grn-v2/international/${editGrnId}/draft`, payload);
        if (andComplete && grnStatus !== 'pending_verification') {
          await api.post(`/grn-v2/international/${editGrnId}/submit-for-verification`);
          toast({ title: 'Success', description: 'GRN updated and submitted for warehouse verification.' });
        } else {
          toast({ title: 'Success', description: `GRN ${grnNumber} updated` });
        }
      } else {
        const res = await api.post('/grn-v2/international', payload);
        const grnId = res.data.id;

        if (andComplete) {
          await api.post(`/grn-v2/international/${grnId}/submit-for-verification`);
          toast({ title: 'Success', description: 'GRN sent to the warehouse for review. Stock updates only after warehouse approval.' });
        } else {
          toast({ title: 'Success', description: 'International GRN saved as draft' });
        }
      }
      
      setMarginBlock(null);
      setSecurityCode('');
      navigate('/hq/purchase/grn');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.error === 'margin_reduction_blocked') {
        setMarginBlock({ violations: detail.violations || [], complete: andComplete });
        setSaving(false);
        return;
      }
      toast({ 
        title: 'Error', 
        description: detail || 'Failed to save GRN',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const currencySymbol = currencies.find(c => c.value === currency)?.symbol || currency;

  return (
    <div className="pb-24 bg-gray-50 min-h-screen" data-testid="create-grn-international-page">
      <Toaster />

      {/* Sticky Header */}
      <div className="sticky top-0 z-30 bg-white border-b shadow-sm px-4 lg:px-8 py-3">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate('/hq/purchase/grn')} data-testid="grn-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-gray-800 flex items-center gap-2 truncate">
                <Globe className="w-5 h-5 text-blue-600" />
                {isEdit ? `Edit International GRN — ${grnNumber}` : 'Create International GRN'}
              </h1>
              <p className="text-xs text-gray-500 truncate">
                {vendor ? (vendor.vendor_name || vendor.name) : 'Select vendor'} · {items.length} item(s) · Currency {currency} @ {conversionRate}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wide text-gray-500">Grand Total</p>
              <p className="text-xl font-bold text-blue-600" data-testid="sticky-grand-total-intl">₹{Math.round(Number(grandTotal || 0)).toLocaleString('en-IN')}</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => handleSave(false)} disabled={saving || loading} data-testid="grn-save-draft-btn">
                <Save className="w-4 h-4 mr-1" /> {isEdit ? 'Update' : 'Save Draft'}
              </Button>
              {!(isEdit && grnStatus === 'pending_verification') && (
                <Button size="sm" onClick={() => handleSave(true)} disabled={saving || loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="grn-complete-btn">
                  <CheckCircle className="w-4 h-4 mr-1" /> {isEdit ? 'Update & Send' : 'Save & Send'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 lg:p-8">
      <div className="space-y-6">
        {/* Full-width Main Info */}
        <div className="space-y-6">
          {/* Vendor & Warehouse */}
          <Card className="bg-white border-gray-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-500" />
                Vendor & Destination
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>International Vendor *</Label>
                <Select
                  value={vendor?.vendor_id || vendor?.id || ''}
                  onValueChange={(val) => {
                    const v = vendors.find(x => x.id === val);
                    if (v) {
                      setVendor({
                        vendor_id: v.id,
                        vendor_code: v.code,
                        vendor_name: v.name,
                        country: v.address?.country || 'International'
                      });
                    }
                  }}
                  disabled={!!poId}
                >
                  <SelectTrigger data-testid="grn-vendor-selector">
                    <SelectValue placeholder="Select vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    {vendors.map(v => (
                      <SelectItem key={v.id} value={v.id}>
                        {v.name} ({v.code})
                        {v.address?.country && <span className="text-gray-400 ml-2">- {v.address.country}</span>}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Destination Warehouse *</Label>
                <Select
                  value={warehouse?.id || ''}
                  onValueChange={(val) => setWarehouse(warehouses.find(w => w.id === val))}
                  disabled={!!poId}
                >
                  <SelectTrigger data-testid="grn-warehouse-selector">
                    <SelectValue placeholder="Select warehouse" />
                  </SelectTrigger>
                  <SelectContent>
                    {warehouses.map(w => (
                      <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Receipt Details */}
          <Card className="bg-white border-gray-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-500" />
                Receipt Details
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Received Date *</Label>
                <Input
                  type="date"
                  value={receivedDate}
                  onChange={(e) => setReceivedDate(e.target.value)}
                  data-testid="grn-received-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Imported Date</Label>
                <Input
                  type="date"
                  value={importedDate}
                  onChange={(e) => setImportedDate(e.target.value)}
                  data-testid="grn-imported-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Invoice Number</Label>
                <Input
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  placeholder="INV-XXXX"
                  data-testid="grn-invoice-number"
                />
              </div>
              <div className="space-y-2">
                <Label>BOL / AWB Ref</Label>
                <Input
                  value={otherDocRef}
                  onChange={(e) => setOtherDocRef(e.target.value)}
                  placeholder="Bill of Lading / Air Waybill"
                  data-testid="grn-doc-ref"
                />
              </div>
            </CardContent>
          </Card>

          {/* Currency & FX Rate */}
          <Card className="bg-white border-gray-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-500" />
                Currency & Conversion
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Currency *</Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger data-testid="grn-currency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Conversion Rate (1 {currency} = INR) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={conversionRate}
                  onChange={(e) => setConversionRate(e.target.value)}
                  data-testid="grn-fx-rate"
                />
              </div>
              <div className="space-y-2 flex items-end">
                <div className="text-sm text-gray-500">
                  Example: {currencySymbol}100 = ₹{(100 * parseFloat(conversionRate || 0)).toFixed(2)}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Landed Costs Section */}
          <Card className="bg-blue-50 border-blue-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2 text-blue-800">
                <Calculator className="w-5 h-5" />
                Landed Costs (Distributed to Items)
              </CardTitle>
              <p className="text-sm text-blue-600">These costs will be distributed proportionally across items based on quantity</p>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Freight */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-blue-700 flex items-center gap-1">
                    <Ship className="w-3 h-3" /> Ocean Freight
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={oceanFreight}
                    onChange={(e) => setOceanFreight(e.target.value)}
                    className="bg-white"
                    data-testid="grn-ocean-freight"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-blue-700 flex items-center gap-1">
                    <Plane className="w-3 h-3" /> Air Freight
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={airFreight}
                    onChange={(e) => setAirFreight(e.target.value)}
                    className="bg-white"
                    data-testid="grn-air-freight"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-blue-700">Other Freight</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={otherFreightCharges}
                    onChange={(e) => setOtherFreightCharges(e.target.value)}
                    className="bg-white"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-blue-700 flex items-center gap-1">
                    <Truck className="w-3 h-3" /> Transport
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={transportCharges}
                    onChange={(e) => setTransportCharges(e.target.value)}
                    className="bg-white"
                    data-testid="grn-transport"
                  />
                </div>
              </div>

              {/* Clearing & Agent */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-blue-700">C&F Charges</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={cfCharges}
                    onChange={(e) => setCfCharges(e.target.value)}
                    className="bg-white"
                    data-testid="grn-cf-charges"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-blue-700">CHA Charges</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={chaCharges}
                    onChange={(e) => setChaCharges(e.target.value)}
                    className="bg-white"
                    data-testid="grn-cha-charges"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-blue-700">FSSAI Sampling</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={fssaiSamplingCharges}
                    onChange={(e) => setFssaiSamplingCharges(e.target.value)}
                    className="bg-white"
                    data-testid="grn-fssai"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-blue-700">Other Landed</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={otherLandedCharges}
                    onChange={(e) => setOtherLandedCharges(e.target.value)}
                    className="bg-white"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-blue-200">
                <span className="text-blue-800 font-medium">Total Header Landed Costs:</span>
                <span className="text-xl font-bold text-blue-900">₹{totalHeaderLanded.toFixed(2)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Items */}
          <Card className="bg-white border-gray-200">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="w-5 h-5 text-orange-500" />
                Line Items ({items.length})
              </CardTitle>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setProductDialogOpen(true)}
                className="border-orange-300 text-orange-600 hover:bg-orange-50"
                data-testid="grn-add-product-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Add Product
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {items.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No items added. Click "Add Product" to start.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="text-xs">Product</TableHead>
                        <TableHead className="text-xs text-center">Qty</TableHead>
                        <TableHead className="text-xs text-right">Unit ({currency})</TableHead>
                        <TableHead className="text-xs text-right">Unit (INR)</TableHead>
                        <TableHead className="text-xs text-center">Duty %</TableHead>
                        <TableHead className="text-xs text-center">GST %</TableHead>
                        <TableHead className="text-xs text-right">Line Total</TableHead>
                        <TableHead className="text-xs text-right">Avg Cost</TableHead>
                        <TableHead className="text-xs w-12"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {items.map((item, idx) => {
                        const calc = calculateItemTotals(item);
                        return (
                          <React.Fragment key={idx}>
                          <TableRow className="border-gray-100">
                            <TableCell className="py-2">
                              <div>
                                <p className="font-medium text-sm">{item.product_name}</p>
                                <p className="text-xs text-gray-400">{item.sku}{item.hsn_code ? ` · HSN ${item.hsn_code}` : ''}</p>
                                {item.scanned_barcode && (
                                  <p className="text-xs text-indigo-600 font-mono" title="Scanned barcode">📷 {item.scanned_barcode}</p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="py-2">
                              <Input
                                type="number"
                                min="0"
                                value={item.accepted_qty}
                                onChange={(e) => updateItem(idx, 'accepted_qty', e.target.value)}
                                className="w-16 text-center text-sm h-8"
                                data-testid={`grn-intl-qty-${idx}`}
                              />
                            </TableCell>
                            <TableCell className="py-2">
                              <Input
                                type="number"
                                step="0.01"
                                value={item.unit_price_fcy}
                                onChange={(e) => updateItem(idx, 'unit_price_fcy', e.target.value)}
                                className="w-20 text-right text-sm h-8"
                                data-testid={`grn-intl-price-${idx}`}
                              />
                            </TableCell>
                            <TableCell className="py-2 text-right text-sm text-gray-600">
                              ₹{calc.unitPriceInr}
                            </TableCell>
                            <TableCell className="py-2">
                              <Input
                                type="number"
                                step="0.1"
                                value={item.custom_duty_percent}
                                onChange={(e) => updateItem(idx, 'custom_duty_percent', e.target.value)}
                                className="w-16 text-center text-sm h-8"
                                data-testid={`grn-intl-duty-${idx}`}
                              />
                            </TableCell>
                            <TableCell className="py-2">
                              <Input
                                type="number"
                                step="0.1"
                                value={item.gst_percent}
                                onChange={(e) => updateItem(idx, 'gst_percent', e.target.value)}
                                className="w-16 text-center text-sm h-8"
                                data-testid={`grn-intl-gst-${idx}`}
                              />
                            </TableCell>
                            <TableCell className="py-2 text-right font-medium text-sm">
                              ₹{calc.lineTotal}
                            </TableCell>
                            <TableCell className="py-2 text-right text-sm text-emerald-600 font-medium">
                              ₹{calc.avgBaseCost}
                            </TableCell>
                            <TableCell className="py-2">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => removeItem(idx)}
                                className="h-8 w-8 text-red-500 hover:text-red-700"
                                data-testid={`grn-intl-remove-${idx}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                          {/* Discount row (Option A - per unit + line total).
                              Sits right below the GST cell visually with 6 bidirectional fields. */}
                          <TableRow key={`${idx}-disc`} className="bg-blue-50/50 border-b">
                            <TableCell colSpan={9} className="py-2">
                              <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                                <div>
                                  <label
                                    className="text-[11px] text-blue-700 font-medium block"
                                    title="Discount % (per line - standard vendor discount)"
                                  >
                                    Disc %
                                  </label>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={item.discount_percent ?? 0}
                                    onChange={(e) => updateDiscountFieldIntl(idx, 'discount_percent', e.target.value)}
                                    className="h-8 text-sm"
                                    data-testid={`grn-intl-disc-pct-${idx}`}
                                  />
                                </div>
                                <div>
                                  <label
                                    className="text-[11px] text-blue-700 font-medium block"
                                    title="Discount Amount - per unit ( = Unit INR x Disc % )"
                                  >
                                    Disc Amt
                                  </label>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={item.discount_amount ?? 0}
                                    onChange={(e) => updateDiscountFieldIntl(idx, 'discount_amount', e.target.value)}
                                    className="h-8 text-sm"
                                    data-testid={`grn-intl-disc-amt-${idx}`}
                                  />
                                </div>
                                <div>
                                  <label
                                    className="text-[11px] text-blue-700 font-medium block"
                                    title="Gross Discount Amount - line total (= Qty x Disc Amt)"
                                  >
                                    Gross Disc Amt
                                  </label>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={item.gross_discount_amount ?? 0}
                                    onChange={(e) => updateDiscountFieldIntl(idx, 'gross_discount_amount', e.target.value)}
                                    className="h-8 text-sm bg-white font-medium"
                                    data-testid={`grn-intl-gross-disc-${idx}`}
                                  />
                                </div>
                                <div>
                                  <label
                                    className="text-[11px] text-indigo-700 font-medium block"
                                    title="Scheme Discount % - applied on post-discount unit price"
                                  >
                                    Sch Disc %
                                  </label>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={item.scheme_discount_percent ?? 0}
                                    onChange={(e) => updateDiscountFieldIntl(idx, 'scheme_discount_percent', e.target.value)}
                                    className="h-8 text-sm"
                                    data-testid={`grn-intl-sch-pct-${idx}`}
                                  />
                                </div>
                                <div>
                                  <label
                                    className="text-[11px] text-indigo-700 font-medium block"
                                    title="Scheme Discount Amount - per unit ( = (Unit - Disc Amt) x Sch % )"
                                  >
                                    Sch Disc Amt
                                  </label>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={item.scheme_discount_amount ?? 0}
                                    onChange={(e) => updateDiscountFieldIntl(idx, 'scheme_discount_amount', e.target.value)}
                                    className="h-8 text-sm"
                                    data-testid={`grn-intl-sch-amt-${idx}`}
                                  />
                                </div>
                                <div>
                                  <label
                                    className="text-[11px] text-indigo-700 font-medium block"
                                    title="Gross Scheme Discount Amount - line total (= Qty x Sch Disc Amt)"
                                  >
                                    Gross Sch Disc Amt
                                  </label>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={item.gross_scheme_discount_amount ?? 0}
                                    onChange={(e) => updateDiscountFieldIntl(idx, 'gross_scheme_discount_amount', e.target.value)}
                                    className="h-8 text-sm bg-white font-medium"
                                    data-testid={`grn-intl-gross-sch-${idx}`}
                                  />
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Batch Info for each item */}
              {items.length > 0 && (
                <div className="p-4 border-t border-gray-200 space-y-4">
                  <h4 className="font-medium text-gray-700 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-500" />
                    Batch Details (Required)
                  </h4>
                  {items.map((item, idx) => (
                    <div key={idx} className="bg-gray-50 rounded-lg p-3">
                      <p className="font-medium text-sm mb-2">{item.product_name}</p>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        <div>
                          <Label className="text-xs">Batch/Lot No. *</Label>
                          <Input
                            value={item.lot_batch_number}
                            onChange={(e) => updateItem(idx, 'lot_batch_number', e.target.value)}
                            placeholder="LOT001"
                            className="h-8 text-sm"
                            data-testid={`grn-batch-${idx}`}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Mfg Date</Label>
                          <Input
                            type="date"
                            value={item.manufacturing_date}
                            onChange={(e) => updateItem(idx, 'manufacturing_date', e.target.value)}
                            className="h-8 text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Expiry Date *</Label>
                          <Input
                            type="date"
                            value={item.expiry_date}
                            onChange={(e) => updateItem(idx, 'expiry_date', e.target.value)}
                            className="h-8 text-sm"
                            data-testid={`grn-expiry-${idx}`}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">MRP *</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={item.mrp}
                            onChange={(e) => updateItem(idx, 'mrp', e.target.value)}
                            className="h-8 text-sm"
                            data-testid={`grn-mrp-${idx}`}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Rejected Qty</Label>
                          <Input
                            type="number"
                            min="0"
                            value={item.rejected_qty}
                            onChange={(e) => updateItem(idx, 'rejected_qty', e.target.value)}
                            className="h-8 text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Full-width Bottom Summary Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* PO Reference + Notes */}
          <div className="space-y-4">
            {po && (
              <Card className="bg-purple-50 border-purple-200">
                <CardContent className="p-4">
                  <h3 className="font-semibold text-purple-800 mb-2">Linked PO</h3>
                  <p className="text-lg font-bold text-purple-900">{po.po_number}</p>
                  <Badge className="mt-2 bg-purple-200 text-purple-800">{po.status}</Badge>
                </CardContent>
              </Card>
            )}
            <Card className="bg-white">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Additional notes..."
                  rows={4}
                />
              </CardContent>
            </Card>
          </div>

          {/* Bill Adjustments */}
          <Card className="bg-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Bill Adjustments</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-gray-500">Bill Disc %</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={billDiscountPercent}
                  onChange={(e) => setBillDiscountPercent(e.target.value)}
                  className="text-right"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Bill Disc ₹</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={billDiscountAmount}
                  onChange={(e) => setBillDiscountAmount(e.target.value)}
                  className="text-right"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-gray-500">Round Off</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={roundOff}
                  onChange={(e) => setRoundOff(e.target.value)}
                  className="text-right"
                  data-testid="grn-round-off"
                />
              </div>
            </CardContent>
          </Card>

          {/* Grand Summary */}
          <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-blue-800">Grand Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal ({currency})</span>
                <span>{currencySymbol}{grandTotals.subtotalFcy.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Subtotal (INR)</span>
                <span>₹{grandTotals.subtotalInr.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Total Duties</span>
                <span>₹{grandTotals.totalDuty.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Total GST</span>
                <span>₹{grandTotals.totalGst.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-blue-600">
                <span>Header Landed Costs</span>
                <span>₹{totalHeaderLanded.toFixed(2)}</span>
              </div>
              <div className="border-t pt-2 flex justify-between text-xl font-bold">
                <span>Grand Total</span>
                <span className="text-emerald-600" data-testid="grn-grand-total-intl">₹{Math.round(Number(grandTotal || 0)).toLocaleString('en-IN')}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      </div>

      {/* Product Search Dialog */}
      {/* Margin reduction - security code required */}
      <Dialog open={Boolean(marginBlock)} onOpenChange={(open) => !open && setMarginBlock(null)}>
        <DialogContent className="bg-white max-w-lg" data-testid="margin-security-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Critical: margin reduction
            </DialogTitle>
            <DialogDescription>
              This GRN reduces the margin on the products below. Enter the active Super Admin
              security code to proceed - every change is logged.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 rounded-lg border border-red-200 bg-red-50 p-3">
            {(marginBlock?.violations || []).map(v => (
              <div key={v.product_id} className="text-sm" data-testid={`margin-violation-${v.product_id}`}>
                <span className="font-medium text-gray-800">{v.product_name}</span>
                <span className="text-gray-500"> ({v.sku})</span>
                <span className="block text-red-700">
                  {v.old_margin_percent}% → {v.new_margin_percent}% (down {v.drop} points)
                </span>
              </div>
            ))}
          </div>
          <div>
            <Label className="text-gray-700">Super Admin security code</Label>
            <Input
              value={securityCode}
              onChange={(e) => setSecurityCode(e.target.value.toUpperCase())}
              placeholder="6-character code"
              maxLength={6}
              className="font-mono tracking-[0.3em] text-center text-lg"
              data-testid="margin-security-code-input"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarginBlock(null)} data-testid="margin-security-cancel">Cancel</Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              disabled={securityCode.length < 6 || saving}
              onClick={() => handleSave(marginBlock?.complete, securityCode)}
              data-testid="margin-security-submit"
            >
              Authorise & Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Product Dialog */}
      <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Product</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                placeholder="Search by name or SKU..."
                className="pl-10"
                autoFocus
                data-testid="product-search-input"
              />
            </div>
            <div className="max-h-64 overflow-y-auto space-y-2">
              {searchLoading ? (
                <p className="text-center text-gray-500 py-4">Searching...</p>
              ) : searchResults.length === 0 ? (
                <p className="text-center text-gray-500 py-4">
                  {productSearch.length < 2 ? 'Type at least 2 characters' : 'No products found'}
                </p>
              ) : (
                searchResults.map(p => (
                  <div
                    key={p.id}
                    onClick={() => handleAddProduct(p)}
                    className="p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50"
                    data-testid={`product-option-${p.id}`}
                  >
                    <p className="font-medium"><HighlightText text={p.name} query={productSearch} /></p>
                    <p className="text-sm text-gray-500"><HighlightText text={p.sku} query={productSearch} /> | HSN: {p.hsn_code || 'N/A'}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreateGRNInternationalPage;
